/**
 * Reusable async-state views: Loading spinner, Skeleton blocks, Empty state, and
 * Error state. Used by every page/component that calls the mock API so loading,
 * empty, and error states are consistent across the booking module.
 */
import React from "react";
import "./states.css";

/** Centered spinner with an optional label. */
export function LoadingSpinner({ label = "Loading…", compact = false }) {
  return (
    <div className={compact ? "bk-state bk-state--compact" : "bk-state"} role="status" aria-live="polite">
      <span className="bk-spinner" aria-hidden="true" />
      {label && <p className="bk-state__text">{label}</p>}
    </div>
  );
}

/**
 * Skeleton placeholder blocks (shimmer). Render `count` card-shaped rows.
 * @param {{count?: number, variant?: 'card'|'line'}} props
 */
export function Skeleton({ count = 3, variant = "card" }) {
  return (
    <div className="bk-skeleton-list" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`bk-skeleton bk-skeleton--${variant}`} />
      ))}
    </div>
  );
}

/** Empty state with an icon, message, and optional action. */
export function EmptyState({
  icon = "fas fa-plane-slash",
  title = "Nothing here yet",
  message = "",
  action = null,
}) {
  return (
    <div className="bk-state" role="status">
      <i className={`${icon} bk-state__icon`} aria-hidden="true" />
      <h3 className="bk-state__title">{title}</h3>
      {message && <p className="bk-state__text">{message}</p>}
      {action}
    </div>
  );
}

/** Error state with a retry button. */
export function ErrorState({
  title = "Something went wrong",
  message = "We couldn't complete that request. Please try again.",
  onRetry = null,
}) {
  return (
    <div className="bk-state bk-state--error" role="alert">
      <i className="fas fa-exclamation-triangle bk-state__icon" aria-hidden="true" />
      <h3 className="bk-state__title">{title}</h3>
      {message && <p className="bk-state__text">{message}</p>}
      {onRetry && (
        <button type="button" className="bk-retry-btn" onClick={onRetry}>
          <i className="fas fa-redo" aria-hidden="true" /> Try again
        </button>
      )}
    </div>
  );
}

const StateViews = { LoadingSpinner, Skeleton, EmptyState, ErrorState };
export default StateViews;
